import { FC, useState } from 'react';
import { IonButton, IonDatetime, IonPopover } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { SimulateRecordingButton } from '../../components/SimulateRecordingButton';
import { TestRecordingToolsModal } from './TestRecordingToolsModal';
import { RecordingService } from './RecordingService';
import { CareMapState } from '../../caremap/CareMapState';
import dayjs from 'dayjs';

export const RecordingDevTools: FC = () => {
    const recordingService = useInstance(RecordingService);
    const careMapState = useInstance(CareMapState);
    const [showTestRecordingModal, setShowTestRecordingModal] = useState(false);
    const [customDateTime, setCustomDateTime] = useState<string | null>(null);
    const [showDatePopover, setShowDatePopover] = useState(false);

    const uploadAudioFile = (extras: { summaryPrompt?: 'intro' | 'appointment' }) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '*/*';
        input.multiple = false;

        input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) {
                return;
            }

            try {
                // Read audio duration
                const audioUrl = URL.createObjectURL(file);
                const audio = new Audio(audioUrl);

                const duration = await new Promise<number>((resolve, reject) => {
                    audio.addEventListener('loadedmetadata', () => {
                        URL.revokeObjectURL(audioUrl);
                        resolve(audio.duration);
                    });
                    audio.addEventListener('error', () => {
                        URL.revokeObjectURL(audioUrl);
                        reject(new Error('Failed to load audio metadata'));
                    });
                });

                const arrayBuffer = await file.arrayBuffer();

                await careMapState.ensureCareMapExists();

                const recordingData = {
                    file: arrayBuffer,
                    mimeType: file.type,
                    msDuration: Math.round(duration * 1000),
                    createdAtOverride: customDateTime ? dayjs(customDateTime).utc().toISOString() : undefined
                };

                await recordingService.add(recordingData, {
                    skipAIProcessing: false,
                    autoSaved: true,
                    ...extras
                });
            } catch (error) {
                console.error('Failed to upload audio:', error);
            }
        };

        input.click();
    };

    const formatDisplayDate = (isoString: string | null) => {
        if (!isoString) {
            return 'Now';
        }
        return dayjs(isoString).format('MMM D, YYYY h:mm A');
    };

    return (
        <>
            <div style={{marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                <span>Upload Date:</span>
                <IonButton
                    id="dev-tools-date-trigger"
                    fill="outline"
                    color="medium"
                    size="small"
                    data-id="custom-date-button"
                    onClick={() => setShowDatePopover(true)}
                >
                    {formatDisplayDate(customDateTime)}
                </IonButton>
                {customDateTime && (
                    <IonButton
                        fill="clear"
                        color="medium"
                        size="small"
                        data-id="clear-date-button"
                        onClick={() => setCustomDateTime(null)}
                    >
                        Clear
                    </IonButton>
                )}
                <IonButton
                    fill="outline"
                    color="secondary"
                    size="small"
                    data-id="upload-recording-button"
                    onClick={() => uploadAudioFile({})}
                >
                    Add Recording
                </IonButton>
                <IonButton
                    fill="outline"
                    color="tertiary"
                    size="small"
                    data-id="upload-intro-recording-button"
                    onClick={() => uploadAudioFile({ summaryPrompt: 'intro' })}
                >
                    Add Intro Recording
                </IonButton>
            </div>

            <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                <IonButton
                    fill="outline"
                    color="secondary"
                    size="small"
                    data-id="open-test-recording-modal-button"
                    onClick={() => setShowTestRecordingModal(true)}
                >
                    Test Recording Tools
                </IonButton>
                <SimulateRecordingButton />
            </div>

            <IonPopover
                isOpen={showDatePopover}
                onDidDismiss={() => setShowDatePopover(false)}
                trigger="dev-tools-date-trigger"
                data-id="date-time-popover"
            >
                <div style={{ padding: '16px' }}>
                    <IonDatetime
                        value={customDateTime || undefined}
                        onIonChange={e => {
                            setCustomDateTime(e.detail.value as string);
                            setShowDatePopover(false);
                        }}
                        presentation="date-time"
                        data-id="custom-date-time-picker"
                    />
                </div>
            </IonPopover>

            <TestRecordingToolsModal
                isOpen={showTestRecordingModal}
                onDismiss={() => setShowTestRecordingModal(false)}
            />
        </>
    );
};
